import {getAuthToken} from '../auth';
import {apiPost, extractFilenameFromPath, getApiBaseUrl} from '../../utils/api';
import {config} from '../../config/app-config';
import {createModal} from './ui-elements';
import {updatePendingChanges, getFileForChange} from './content-store';
import {setupDocumentDownloadHandlers} from './content-editor/element-setup';


export function openDocumentEditor(element, options = {}) {
    const pageTitle = element.getAttribute('data-page') || 'Home';
    const tagId = element.getAttribute('data-content');
    const blockName = element.getAttribute('data-block-name') || findBlockName(element);
    const dataAttribute = element.getAttribute('data-attribute') || 'document';

    let currentDocumentPath = element.getAttribute('data-media-path') || '';
    let currentDocumentName = currentDocumentPath ? extractFilenameFromPath(currentDocumentPath) : '';

    const modalContent = `
    <h3>Edit Document</h3>
    ${currentDocumentName ? `<div class="current-document">Current document: ${currentDocumentName}</div>` : ''}
    <div class="file-input-container">
      <input type="file" id="document-upload" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip">
      <label for="document-upload" class="file-label">Choose a document</label>
    </div>
    <div class="file-name" id="file-name"></div>
    ${blockName ? `<div class="block-info">Block: ${blockName}</div>` : ''}
  `;

    const modal = createModal(modalContent);

    const fileInput = document.getElementById('document-upload');
    const fileName = document.getElementById('file-name');

    fileInput.addEventListener('change', function () {
        if (fileInput.files.length > 0) {
            fileName.textContent = fileInput.files[0].name;
            modal.enableSave();
        } else {
            fileName.textContent = '';
            modal.disableSave();
        }
    });

    modal.on('save', () => {
        if (fileInput.files.length > 0) {
            // ИЗМЕНЕНИЕ: Теперь только сохраняем в pending changes, не загружаем на сервер
            saveDocumentChangeLocal(element, {
                pageTitle,
                tagId,
                file: fileInput.files[0],
                blockName,
                dataAttribute
            });
        }
        modal.close();
    });
}

// НОВАЯ ФУНКЦИЯ: Локальное сохранение документа (только в pending changes)
export function saveDocumentChangeLocal(element, data) {
    console.log(`Saving document change locally for ${data.tagId} on page: ${data.pageTitle}`);

    // Обновляем pending changes с файлом
    updatePendingChanges('document', data.tagId, {
        pageTitle: data.pageTitle,
        tagId: data.tagId,
        file: data.file,
        fileName: data.file.name,
        element: element,
        blockName: data.blockName,
        dataAttribute: data.dataAttribute || 'document'
    });

    // Показываем индикатор что документ будет загружен
    element.classList.add('content-modified');

    // Если это cooperation-docs блок, обновляем tooltip
    if (element.classList.contains('cooperation-docs')) {
        const dropdownIcon = element.querySelector('img[src*="Dropdown"]');
        if (dropdownIcon) {
            dropdownIcon.title = `Will upload: ${data.file.name} (awaiting publish)`;
            dropdownIcon.style.cursor = 'pointer';
            dropdownIcon.style.opacity = '0.7'; // Показываем что это превью
        }
    }

    // Показываем кнопку publish
    const publishBtn = document.getElementById(config.editor.selectors.publishButton);
    if (publishBtn) {
        publishBtn.style.display = 'block';
    }

    console.log(`Document prepared for upload: ${data.file.name}, awaiting publish`);
}

// ИЗМЕНЕНИЕ: Теперь эта функция вызывается только из publish manager
export function saveDocumentChange(element, data) {
    console.log(`Publishing document change for ${data.tagId} on page: ${data.pageTitle}`);

    const token = getAuthToken();
    if (!token) {
        return Promise.reject(new Error('Authentication required'));
    }

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('page_title', data.pageTitle);
    formData.append('tag_id', data.tagId);
    formData.append('tag', data.tagId);
    formData.append('order', 1);
    formData.append('data_attribute', data.dataAttribute || 'document');

    if (data.blockName) {
        formData.append('block_name', data.blockName);
    }

    element.classList.add('loading');

    return apiPost(config.api.paths.contentDocument, formData)
        .then(result => {
            console.log(`✅ Document uploaded successfully for ${data.tagId}`);

            if (result && result.media_path) {
                // Сохраняем path как атрибут
                element.setAttribute('data-media-path', result.media_path);
                element.classList.remove('content-modified', 'loading');

                // Обновляем cooperation-docs если есть
                if (element.classList.contains('cooperation-docs')) {
                    const dropdownIcon = element.querySelector('img[src*="Dropdown"]');
                    if (dropdownIcon) {
                        dropdownIcon.title = `Download document: ${data.fileName}`;
                        dropdownIcon.style.cursor = 'pointer';
                        dropdownIcon.style.opacity = '1'; // Убираем превью стиль
                        dropdownIcon.setAttribute('data-document-path', result.media_path);
                    }
                }

                setTimeout(() => {
                    setupDocumentDownloadHandlers();
                }, 100);

                return result;
            }
        })
        .catch(error => {
            console.error(`❌ Error uploading document for ${data.tagId}:`, error);
            element.classList.remove('loading');

            // Показываем ошибку пользователю
            if (window.showErrorNotification) {
                if (error.response && error.response.data && error.response.data.detail) {
                    window.showErrorNotification(`Document upload error: ${error.response.data.detail}`);
                } else {
                    window.showErrorNotification('Document upload error. Check file format.');
                }
            }

            throw error;
        });
}

export async function publishDocumentChange(tagId, change) {
    try {
        // Находим актуальный элемент в DOM
        let targetElement = change.element;

        // Если элемент недоступен, ищем по tagId
        if (!targetElement || !targetElement.classList) {
            targetElement = document.querySelector(`[data-content="${tagId}"]`);
        }

        if (!targetElement) {
            throw new Error(`Element with tagId ${tagId} not found`);
        }

        // ИСПРАВЛЕНИЕ: Получаем файл из отдельного хранилища
        const file = getFileForChange('document', tagId);

        if (!file || !(file instanceof File)) {
            console.error('Invalid file for tagId:', tagId, 'file:', file);
            throw new Error(`Invalid file for ${tagId}: expected File object, got ${typeof file}`);
        }

        console.log(`Publishing document ${tagId}, file:`, file.name, file.type, file.size, 'bytes');

        // Создаем новый объект change с правильным файлом
        const changeWithFile = {
            ...change,
            file: file
        };

        // Теперь публикуем файл на сервер
        const result = await saveDocumentChange(targetElement, changeWithFile);

        if (targetElement.classList) {
            targetElement.setAttribute('data-original-media-path', result.media_path);
            targetElement.classList.remove('content-modified');
        }

        return tagId;
    } catch (error) {
        console.error(`Failed to publish document change for ${tagId}:`, error);
        throw error;
    }
}

function findBlockName(element) {
    let parent = element.parentElement;
    while (parent) {
        if (parent.hasAttribute('data-block-name')) {
            return parent.getAttribute('data-block-name');
        }
        parent = parent.parentElement;
    }
    return '';
}